import React, { useEffect, useState } from "react";
import { logsApi } from "../services/logsApi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  Smartphone,
  Monitor,
  Tablet,
  Users,
  RefreshCw,
  Calendar,
  TrendingUp,
  Activity,
  AlertCircle,
} from "lucide-react";
import Skeleton, { SkeletonCard, SkeletonChart } from "./ui/Skeleton";

const t = {
  primary: "#7c3aed",
  primaryMid: "#a78bfa",
  primaryLight: "#ede9fe",
  surface: "#ffffff",
  border: "#e9e5f5",
  danger: "#ef4444",
  dangerBg: "#fee2e2",
  warn: "#f59e0b",
  warnBg: "#fef3c7",
  info: "#3b82f6",
  infoBg: "#dbeafe",
  success: "#10b981",
  successBg: "#d1fae5",
  text: {
    primary: "#1e1b4b",
    secondary: "#6b7280",
    muted: "#9ca3b8",
    inverse: "#ffffff",
  },
};

const PLATFORM_COLORS: Record<string, string> = {
  admin: "#7c3aed",
  web: "#3b82f6",
  android: "#10b981",
  ios: "#f59e0b",
  unknown: "#94a3b8",
};

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  admin: <Users className="w-5 h-5" style={{ color: PLATFORM_COLORS.admin }} />,
  web: <Monitor className="w-5 h-5" style={{ color: PLATFORM_COLORS.web }} />,
  android: (
    <Smartphone
      className="w-5 h-5"
      style={{ color: PLATFORM_COLORS.android }}
    />
  ),
  ios: <Tablet className="w-5 h-5" style={{ color: PLATFORM_COLORS.ios }} />,
  unknown: (
    <Activity className="w-5 h-5" style={{ color: PLATFORM_COLORS.unknown }} />
  ),
};

const LEVEL_COLORS: Record<string, string> = {
  error: "#ef4444",
  warn: "#f59e0b",
  info: "#7c3aed",
  debug: "#94a3b8",
};

interface PlatformData {
  platform: string;
  count: number;
  percentage: string;
  byLevel: Array<{ key: string; doc_count: number }>;
  byService: Array<{ key: string; doc_count: number }>;
  timeline: Array<{
    key: number;
    key_as_string: string;
    doc_count: number;
    by_service?: { buckets: Array<{ key: string; doc_count: number }> };
  }>;
}

interface PlatformSummary {
  count: number;
  percentage: string;
  levels: Array<{ key: string; doc_count: number }>;
}

interface ServiceByDay {
  key: number;
  key_as_string: string;
  doc_count: number;
  by_service: { buckets: Array<{ key: string; doc_count: number }> };
}

interface StatsData {
  total: number;
  platforms: PlatformData[];
  summary: {
    admin: PlatformSummary;
    web: PlatformSummary;
    android: PlatformSummary;
    ios: PlatformSummary;
  };
  servicesByDay: ServiceByDay[];
}

function PlatformCard({
  platform,
  data,
  total,
}: {
  platform: string;
  data: PlatformSummary;
  total: number;
}) {
  const icon = PLATFORM_ICONS[platform] || PLATFORM_ICONS.unknown;
  const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS.unknown;

  const errorCount = data.levels.find((l) => l.key === "error")?.doc_count || 0;
  const errorRate =
    data.count > 0 ? ((errorCount / data.count) * 100).toFixed(1) : "0.0";

  return (
    <div
      className="rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
      style={{ backgroundColor: t.surface, border: `2px solid ${color}20` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          {icon}
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold" style={{ color: t.text.primary }}>
            {data.count.toLocaleString()}
          </div>
          <div className="text-sm" style={{ color: t.text.secondary }}>
            {data.percentage}% du total
          </div>
        </div>
      </div>

      <h3
        className="text-lg font-semibold mb-3 capitalize"
        style={{ color: t.text.primary }}
      >
        {platform}
      </h3>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: t.text.secondary }}>Taux d'erreur</span>
          <span
            className="font-semibold px-2 py-0.5 rounded"
            style={{
              backgroundColor:
                parseFloat(errorRate) > 5 ? t.dangerBg : t.successBg,
              color: parseFloat(errorRate) > 5 ? t.danger : t.success,
            }}
          >
            {errorRate}%
          </span>
        </div>

        {data.levels.length > 0 && (
          <div className="pt-3 border-t" style={{ borderColor: t.border }}>
            <div
              className="text-xs font-semibold mb-2"
              style={{ color: t.text.secondary }}
            >
              Répartition par niveau
            </div>
            <div className="space-y-1">
              {data.levels.slice(0, 3).map((level) => (
                <div
                  key={level.key}
                  className="flex items-center justify-between text-xs"
                >
                  <span className="flex items-center gap-1">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: LEVEL_COLORS[level.key] || "#94a3b8",
                      }}
                    />
                    <span style={{ color: t.text.secondary }}>{level.key}</span>
                  </span>
                  <span
                    className="font-medium"
                    style={{ color: t.text.primary }}
                  >
                    {level.doc_count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlatformStats() {
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await logsApi.getPlatformStats(
        `${dateRange.start}T00:00:00Z`,
        `${dateRange.end}T23:59:59Z`,
      );
      setData(response.data);
    } catch (err: any) {
      setError(err.message || "Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const pieData =
    data?.platforms.map((p) => ({
      name: p.platform,
      value: p.count,
      percentage: p.percentage,
    })) || [];

  const barData =
    data?.platforms.map((p) => {
      const levels: Record<string, number> = {};
      p.byLevel.forEach((l) => {
        levels[l.key] = l.doc_count;
      });
      return {
        platform: p.platform,
        ...levels,
      };
    }) || [];

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <SkeletonCard />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <SkeletonChart />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div
          className="rounded-xl p-6 flex items-center gap-4"
          style={{
            backgroundColor: t.dangerBg,
            border: `1px solid ${t.danger}`,
          }}
        >
          <AlertCircle className="w-6 h-6" style={{ color: t.danger }} />
          <div>
            <h3 className="font-semibold" style={{ color: t.danger }}>
              Erreur
            </h3>
            <p className="text-sm" style={{ color: t.danger }}>
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: t.text.primary }}>
            Statistiques par Plateforme
          </h1>
          <p className="text-sm mt-1" style={{ color: t.text.secondary }}>
            Analyse des logs par type de plateforme (Admin, Web, Android, iOS)
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:shadow-md"
          style={{ backgroundColor: t.primary, color: t.text.inverse }}
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Date Range Filter */}
      <div
        className="rounded-xl p-4 flex items-center gap-4"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
      >
        <Calendar className="w-5 h-5" style={{ color: t.primary }} />
        <div className="flex items-center gap-3 flex-1">
          <div className="flex items-center gap-2">
            <label
              className="text-sm font-medium"
              style={{ color: t.text.secondary }}
            >
              Du
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ border: `1px solid ${t.border}`, color: t.text.primary }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label
              className="text-sm font-medium"
              style={{ color: t.text.secondary }}
            >
              Au
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ border: `1px solid ${t.border}`, color: t.text.primary }}
            />
          </div>
        </div>
        <div className="text-sm" style={{ color: t.text.secondary }}>
          Total:{" "}
          <span className="font-bold" style={{ color: t.text.primary }}>
            {data?.total.toLocaleString()}
          </span>{" "}
          logs
        </div>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <PlatformCard
          platform="admin"
          data={data!.summary.admin}
          total={data!.total}
        />
        <PlatformCard
          platform="web"
          data={data!.summary.web}
          total={data!.total}
        />
        <PlatformCard
          platform="android"
          data={data!.summary.android}
          total={data!.total}
        />
        <PlatformCard
          platform="ios"
          data={data!.summary.ios}
          total={data!.total}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div
          className="rounded-2xl p-6"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <h3
            className="text-lg font-semibold mb-4"
            style={{ color: t.text.primary }}
          >
            Répartition par Plateforme
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.name}: ${entry.percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={PLATFORM_COLORS[entry.name] || "#94a3b8"}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart - Levels by Platform */}
        <div
          className="rounded-2xl p-6"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <h3
            className="text-lg font-semibold mb-4"
            style={{ color: t.text.primary }}
          >
            Niveaux de Log par Plateforme
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
              <XAxis dataKey="platform" stroke={t.text.secondary} />
              <YAxis stroke={t.text.secondary} />
              <Tooltip />
              <Legend />
              <Bar dataKey="error" fill={LEVEL_COLORS.error} name="Erreurs" />
              <Bar
                dataKey="warn"
                fill={LEVEL_COLORS.warn}
                name="Avertissements"
              />
              <Bar dataKey="info" fill={LEVEL_COLORS.info} name="Info" />
              <Bar dataKey="debug" fill={LEVEL_COLORS.debug} name="Debug" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Timeline Charts */}
      {data?.platforms.map(
        (platform) =>
          platform.timeline.length > 0 && (
            <div
              key={platform.platform}
              className="rounded-2xl p-6"
              style={{
                backgroundColor: t.surface,
                border: `1px solid ${t.border}`,
              }}
            >
              <h3
                className="text-lg font-semibold mb-4 capitalize flex items-center gap-2"
                style={{ color: t.text.primary }}
              >
                {PLATFORM_ICONS[platform.platform]}
                Évolution - {platform.platform}
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={platform.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
                  <XAxis
                    dataKey="key_as_string"
                    stroke={t.text.secondary}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis stroke={t.text.secondary} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="doc_count"
                    stroke={PLATFORM_COLORS[platform.platform]}
                    strokeWidth={2}
                    dot={{ fill: PLATFORM_COLORS[platform.platform] }}
                    name="Logs"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ),
      )}

      {/* Services Consumption by Day */}
      {data?.servicesByDay && data.servicesByDay.length > 0 && (
        <div
          className="rounded-2xl p-6"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <h3
            className="text-lg font-semibold mb-4 flex items-center gap-2"
            style={{ color: t.text.primary }}
          >
            <Activity className="w-5 h-5" style={{ color: t.primary }} />
            Consommation par Service et par Jour
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={data.servicesByDay.map((day) => {
                const serviceData: Record<string, any> = {
                  date: day.key_as_string,
                };
                day.by_service.buckets.forEach((service) => {
                  serviceData[service.key] = service.doc_count;
                });
                return serviceData;
              })}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
              <XAxis
                dataKey="date"
                stroke={t.text.secondary}
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis stroke={t.text.secondary} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              {(() => {
                const allServices = new Set<string>();
                data.servicesByDay.forEach((day) => {
                  day.by_service.buckets.forEach((service) => {
                    allServices.add(service.key);
                  });
                });
                const serviceColors = [
                  "#7c3aed",
                  "#3b82f6",
                  "#10b981",
                  "#f59e0b",
                  "#ef4444",
                  "#8b5cf6",
                  "#06b6d4",
                  "#14b8a6",
                  "#f97316",
                  "#ec4899",
                  "#6366f1",
                  "#84cc16",
                  "#eab308",
                  "#f43f5e",
                  "#a855f7",
                ];
                return Array.from(allServices).map((service, index) => (
                  <Bar
                    key={service}
                    dataKey={service}
                    stackId="services"
                    fill={serviceColors[index % serviceColors.length]}
                    name={service}
                  />
                ));
              })()}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default PlatformStats;
